import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { axiosInstance, API } from "@/lib/axios";
import AuthedImage from "@/components/AuthedImage";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Sparkles,
  ShieldCheck,
  Plus,
  Trash2,
  Pencil,
  Zap,
  AlertTriangle,
  CheckCircle2,
  Camera,
  X,
  Target,
  Undo2,
  Save,
} from "lucide-react";

const ENERGY_TYPES = [
  "Electrical",
  "Pneumatic",
  "Hydraulic",
  "Thermal",
  "Chemical",
  "Mechanical",
  "Gravity",
  "Other",
];

const energyColor = (t) => {
  const map = {
    Electrical: "bg-yellow-100 text-yellow-900",
    Pneumatic: "bg-sky-100 text-sky-900",
    Hydraulic: "bg-blue-100 text-blue-900",
    Thermal: "bg-red-100 text-red-900",
    Chemical: "bg-purple-100 text-purple-900",
    Mechanical: "bg-slate-100 text-slate-900",
    Gravity: "bg-amber-100 text-amber-900",
    Other: "bg-gray-100 text-gray-900",
  };
  return map[t] || map.Other;
};

const emptyEquipment = {
  equipmentName: "",
  aliases: "",
  ppeRequired: "",
  lockoutPoints: [],
  isolationSteps: "",
  notes: "",
};

export default function AdminLOTOPoints() {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEquipment);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/loto/equipment`);
      setEquipment(data.equipment || []);
    } catch (e) {
      toast.error("Failed to load LOTO equipment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const runExtraction = async () => {
    if (extracting) return;
    setExtracting(true);
    const toastId = toast.loading(
      "AI is reading your safety manual and SOPs — this may take up to 60 seconds..."
    );
    try {
      const { data } = await axiosInstance.post(`/loto/extract-from-manual`);
      toast.success(
        `Found ${data.totalExtracted} equipment (${data.created} new, ${data.updated} updated) from ${data.sourcesUsed} document(s)`,
        { id: toastId, duration: 6000 }
      );
      await load();
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "Extraction failed. Make sure a safety manual or SOP has been uploaded.";
      toast.error(msg, { id: toastId, duration: 8000 });
    } finally {
      setExtracting(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyEquipment);
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      equipmentName: e.equipmentName || "",
      aliases: (e.aliases || []).join(", "),
      ppeRequired: e.ppeRequired || "",
      lockoutPoints: e.lockoutPoints || [],
      isolationSteps: (e.isolationSteps || []).join("\n"),
      notes: e.notes || "",
    });
    setDialogOpen(true);
  };

  const addPoint = () => {
    setForm({
      ...form,
      lockoutPoints: [
        ...form.lockoutPoints,
        {
          sequence: form.lockoutPoints.length + 1,
          location: "",
          energyType: "Electrical",
          lockoutMethod: "",
          verification: "",
          notes: "",
        },
      ],
    });
  };

  const updatePoint = (idx, key, val) => {
    const points = [...form.lockoutPoints];
    points[idx] = { ...points[idx], [key]: val };
    setForm({ ...form, lockoutPoints: points });
  };

  const removePoint = (idx) => {
    const points = form.lockoutPoints
      .filter((_, i) => i !== idx)
      .map((p, i) => ({ ...p, sequence: i + 1 }));
    setForm({ ...form, lockoutPoints: points });
  };

  const save = async () => {
    if (!form.equipmentName.trim()) {
      toast.error("Equipment name is required");
      return;
    }
    const payload = {
      equipmentName: form.equipmentName.trim(),
      aliases: form.aliases
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      ppeRequired: form.ppeRequired,
      lockoutPoints: form.lockoutPoints,
      isolationSteps: form.isolationSteps
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      notes: form.notes,
    };
    try {
      if (editing) {
        await axiosInstance.put(`/loto/equipment/${editing.id}`, payload);
        toast.success("Equipment updated and verified");
      } else {
        await axiosInstance.post(`/loto/equipment`, payload);
        toast.success("Equipment created");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete LOTO record for "${e.equipmentName}"?`)) return;
    try {
      await axiosInstance.delete(`/loto/equipment/${e.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const uploadPhoto = async (idx, fileObj) => {
    if (!fileObj) return;
    if (!editing) {
      toast.error("Save the equipment first, then add photos");
      return;
    }
    const seq = form.lockoutPoints[idx]?.sequence;
    if (!seq) return;
    const fd = new FormData();
    fd.append("file", fileObj);
    const tid = toast.loading("Uploading photo...");
    try {
      const { data } = await axiosInstance.post(
        `/loto/equipment/${editing.id}/points/${seq}/photo`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const points = [...form.lockoutPoints];
      points[idx] = {
        ...points[idx],
        photoGridfsId: data.photoGridfsId,
        photoContentType: "image/jpeg",
      };
      setForm({ ...form, lockoutPoints: points });
      toast.success("Photo uploaded", { id: tid });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed", { id: tid });
    }
  };

  const removePhoto = async (idx) => {
    if (!editing) return;
    const seq = form.lockoutPoints[idx]?.sequence;
    if (!seq) return;
    if (!window.confirm("Remove this photo?")) return;
    try {
      await axiosInstance.delete(
        `/loto/equipment/${editing.id}/points/${seq}/photo`
      );
      const points = [...form.lockoutPoints];
      points[idx] = { ...points[idx], photoGridfsId: null };
      setForm({ ...form, lockoutPoints: points });
      toast.success("Photo removed");
    } catch (err) {
      toast.error("Remove failed");
    }
  };

  const photoUrlFor = (id) =>
    id ? `${API}/loto/photo/${id}` : null;

  // ---------- Photo Annotator ----------
  const [annotatorOpen, setAnnotatorOpen] = useState(false);
  const [annotatorIdx, setAnnotatorIdx] = useState(null);
  const [annotatorSrc, setAnnotatorSrc] = useState(null);
  const [markers, setMarkers] = useState([]); // {xPct, yPct}
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  const openAnnotator = async (idx) => {
    const point = form.lockoutPoints[idx];
    if (!point?.photoGridfsId) {
      toast.error("Upload a photo first");
      return;
    }
    setAnnotatorIdx(idx);
    setMarkers([]);
    setAnnotatorOpen(true);
    try {
      const { data } = await axiosInstance.get(
        `/loto/photo/${point.photoGridfsId}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      setAnnotatorSrc(url);
    } catch (e) {
      toast.error("Failed to load photo");
      setAnnotatorOpen(false);
    }
  };

  const handleImgClick = (e) => {
    const img = e.currentTarget;
    const rect = img.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    setMarkers((m) => [...m, { xPct, yPct }]);
  };

  const undoMarker = () => setMarkers((m) => m.slice(0, -1));

  const saveAnnotation = async () => {
    if (markers.length === 0) {
      toast.error("Tap on the photo to add at least one marker");
      return;
    }
    if (annotatorIdx == null || !editing) return;
    const point = form.lockoutPoints[annotatorIdx];
    const tid = toast.loading("Saving annotated photo...");
    try {
      // Fetch the source image with auth so canvas is not tainted
      const imgResp = await axiosInstance.get(
        `/loto/photo/${point.photoGridfsId}`,
        { responseType: "blob" }
      );
      const bmp = await createImageBitmap(imgResp.data);

      const canvas = document.createElement("canvas");
      canvas.width = bmp.width;
      canvas.height = bmp.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bmp, 0, 0);
      const radius = Math.max(canvas.width, canvas.height) * 0.05;
      ctx.strokeStyle = "#dc2626";
      ctx.lineWidth = Math.max(4, radius * 0.15);
      ctx.shadowColor = "rgba(0,0,0,0.6)";
      ctx.shadowBlur = 6;
      markers.forEach((m) => {
        const cx = (m.xPct / 100) * canvas.width;
        const cy = (m.yPct / 100) * canvas.height;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx - radius * 0.4, cy);
        ctx.lineTo(cx + radius * 0.4, cy);
        ctx.moveTo(cx, cy - radius * 0.4);
        ctx.lineTo(cx, cy + radius * 0.4);
        ctx.stroke();
      });
      ctx.shadowBlur = 0;

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.9)
      );
      if (!blob) throw new Error("toBlob returned null");

      const fd = new FormData();
      fd.append("file", blob, `loto_${editing.id}_${point.sequence}_annotated.jpg`);
      const { data } = await axiosInstance.post(
        `/loto/equipment/${editing.id}/points/${point.sequence}/photo`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const points = [...form.lockoutPoints];
      points[annotatorIdx] = {
        ...points[annotatorIdx],
        photoGridfsId: data.photoGridfsId,
        photoContentType: "image/jpeg",
      };
      setForm({ ...form, lockoutPoints: points });
      toast.success("Annotation saved", { id: tid });
      setAnnotatorOpen(false);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || e.message || "Save failed", { id: tid });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate("/admin")}
          data-testid="loto-back-btn"
          className="mb-4 flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </button>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-orange-600" />
              Lockout / Tagout Points
            </h1>
            <p className="text-gray-600 mt-1 max-w-2xl">
              Equipment-specific LOTO procedures extracted from your safety manual and
              SOPs. When a JSA references any of this equipment, its lockout points are
              automatically attached — 29 CFR 1910.147 compliant.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={runExtraction}
              disabled={extracting}
              data-testid="loto-extract-btn"
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {extracting ? "Extracting..." : "Extract from Safety Manual"}
            </Button>
            <Button onClick={openNew} data-testid="loto-new-btn" variant="outline">
              <Plus className="w-4 h-4 mr-2" /> New Equipment
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading...</div>
        ) : equipment.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No LOTO records yet
              </h3>
              <p className="text-gray-600 max-w-md mx-auto mb-4">
                Upload your safety manual and SOPs, then click{" "}
                <strong>Extract from Safety Manual</strong>. Our AI reads your existing
                documents and pulls out equipment-specific lockout points, energy types,
                and PPE — no manual data entry required.
              </p>
              <Button
                onClick={runExtraction}
                disabled={extracting}
                data-testid="loto-extract-empty-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {extracting ? "Extracting..." : "Extract from Safety Manual"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {equipment.map((e) => (
              <Card
                key={e.id}
                data-testid={`loto-card-${e.id}`}
                className="hover:shadow-lg transition"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{e.equipmentName}</CardTitle>
                      {(e.aliases || []).length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          Also known as: {e.aliases.join(", ")}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {e.verifiedBy ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Verified
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-amber-700 border-amber-300"
                        >
                          Needs Review
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {(e.lockoutPoints || [])
                      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                      .map((lp, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-2 text-sm border-l-2 border-orange-300 pl-2"
                        >
                          <span className="font-semibold text-gray-700 w-6">
                            {lp.sequence || idx + 1}.
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge
                                className={`${energyColor(
                                  lp.energyType
                                )} border-0 text-xs`}
                              >
                                <Zap className="w-3 h-3 mr-1" />
                                {lp.energyType}
                              </Badge>
                              <span className="text-gray-900 font-medium">
                                {lp.location}
                              </span>
                            </div>
                            {lp.lockoutMethod && (
                              <div className="text-xs text-gray-600 mt-0.5">
                                {lp.lockoutMethod}
                              </div>
                            )}
                            {lp.verification && (
                              <div className="text-xs text-emerald-700 mt-0.5">
                                ✓ Verify: {lp.verification}
                              </div>
                            )}
                            {lp.photoGridfsId && (
                              <AuthedImage
                                gridfsId={lp.photoGridfsId}
                                alt={`LOTO ${lp.sequence}`}
                                className="rounded border mt-1 max-h-16 object-cover"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                  {e.ppeRequired && (
                    <div className="text-xs text-gray-600 mb-3">
                      <strong>PPE:</strong> {e.ppeRequired}
                    </div>
                  )}
                  {e.sourceDocumentTitle && (
                    <div className="text-xs text-gray-400 mb-3">
                      Source: {e.sourceDocumentTitle}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(e)}
                      data-testid={`loto-edit-btn-${e.id}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Edit / Verify
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => remove(e)}
                      data-testid={`loto-delete-btn-${e.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit / Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit & Verify LOTO Record" : "New LOTO Record"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="equipmentName">Equipment Name *</Label>
              <Input
                id="equipmentName"
                data-testid="loto-form-name"
                value={form.equipmentName}
                onChange={(e) =>
                  setForm({ ...form, equipmentName: e.target.value })
                }
                placeholder="e.g. CNC Press Line #4"
              />
            </div>
            <div>
              <Label htmlFor="aliases">Aliases / Asset Tags (comma-separated)</Label>
              <Input
                id="aliases"
                data-testid="loto-form-aliases"
                value={form.aliases}
                onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                placeholder="e.g. CNC-4200, Press #4"
              />
            </div>
            <div>
              <Label htmlFor="ppe">Required PPE</Label>
              <Input
                id="ppe"
                data-testid="loto-form-ppe"
                value={form.ppeRequired}
                onChange={(e) =>
                  setForm({ ...form, ppeRequired: e.target.value })
                }
                placeholder="e.g. Safety glasses, cut-resistant gloves..."
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Lockout Points</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addPoint}
                  data-testid="loto-form-add-point"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Point
                </Button>
              </div>
              <div className="space-y-3">
                {form.lockoutPoints.map((lp, idx) => (
                  <div key={idx} className="border rounded p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold">Point #{lp.sequence}</span>
                      <button
                        onClick={() => removePoint(idx)}
                        className="text-red-600 hover:text-red-800"
                        data-testid={`loto-remove-point-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Location</Label>
                        <Input
                          value={lp.location || ""}
                          onChange={(e) => updatePoint(idx, "location", e.target.value)}
                          placeholder="e.g. Main disconnect Panel P-4"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Energy Type</Label>
                        <select
                          className="w-full border rounded px-2 py-2 text-sm"
                          value={lp.energyType || "Electrical"}
                          onChange={(e) =>
                            updatePoint(idx, "energyType", e.target.value)
                          }
                        >
                          {ENERGY_TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Lockout Method</Label>
                        <Input
                          value={lp.lockoutMethod || ""}
                          onChange={(e) =>
                            updatePoint(idx, "lockoutMethod", e.target.value)
                          }
                          placeholder="e.g. Padlock on breaker + red danger tag"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Verification</Label>
                        <Input
                          value={lp.verification || ""}
                          onChange={(e) =>
                            updatePoint(idx, "verification", e.target.value)
                          }
                          placeholder="e.g. Try-start test on control panel"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs flex items-center gap-1">
                          <Camera className="w-3 h-3" /> Photo (helps crews find the right disconnect)
                        </Label>
                        {lp.photoGridfsId ? (
                          <div className="relative inline-block mt-1">
                            <AuthedImage
                              gridfsId={lp.photoGridfsId}
                              alt={`LOTO Point ${lp.sequence}`}
                              className="rounded border max-h-32 object-cover"
                              data-testid={`loto-point-photo-${idx}`}
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(idx)}
                              className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow"
                              data-testid={`loto-point-photo-remove-${idx}`}
                              title="Remove photo"
                            >
                              <X className="w-3 h-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openAnnotator(idx)}
                              className="mt-2 flex items-center gap-1 text-xs text-red-700 hover:text-red-800 font-semibold"
                              data-testid={`loto-point-annotate-${idx}`}
                              title="Draw a marker on the exact disconnect"
                            >
                              <Target className="w-3 h-3" /> Mark the disconnect
                            </button>
                          </div>
                        ) : (
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => uploadPhoto(idx, e.target.files?.[0])}
                            className="mt-1 block text-sm"
                            data-testid={`loto-point-photo-input-${idx}`}
                          />
                        )}
                        {!editing && (
                          <p className="text-[10px] text-gray-500 mt-1 italic">
                            Save the equipment first, then re-open to add photos.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="steps">
                Isolation Steps (one per line — from your SOP)
              </Label>
              <Textarea
                id="steps"
                data-testid="loto-form-steps"
                rows={4}
                value={form.isolationSteps}
                onChange={(e) =>
                  setForm({ ...form, isolationSteps: e.target.value })
                }
                placeholder="1. Notify affected employees..."
              />
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              data-testid="loto-form-save"
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {editing ? "Save & Mark Verified" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Annotator Dialog */}
      <Dialog open={annotatorOpen} onOpenChange={setAnnotatorOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-red-600" />
              Mark the Disconnect
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Tap the exact disconnect, valve, or lock point on the photo. A red
              circle will be drawn so crews know precisely where to lock.
            </p>
            <div className="relative inline-block max-w-full">
              {annotatorSrc && (
                <img
                  ref={imgRef}
                  src={annotatorSrc}
                  alt="Annotate"
                  onClick={handleImgClick}
                  className="max-h-[60vh] object-contain rounded border cursor-crosshair select-none"
                  data-testid="loto-annotator-image"
                  draggable={false}
                />
              )}
              {markers.map((m, i) => (
                <div
                  key={i}
                  className="absolute pointer-events-none border-4 border-red-600 rounded-full"
                  style={{
                    left: `calc(${m.xPct}% - 20px)`,
                    top: `calc(${m.yPct}% - 20px)`,
                    width: 40,
                    height: 40,
                    boxShadow: "0 0 0 2px rgba(0,0,0,0.35)",
                  }}
                />
              ))}
              <canvas ref={canvasRef} className="hidden" />
            </div>
            <p className="text-xs text-gray-500">
              {markers.length} marker{markers.length !== 1 ? "s" : ""} added
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={undoMarker}
              disabled={markers.length === 0}
              data-testid="loto-annotator-undo"
            >
              <Undo2 className="w-4 h-4 mr-2" /> Undo
            </Button>
            <Button
              variant="outline"
              onClick={() => setMarkers([])}
              disabled={markers.length === 0}
              data-testid="loto-annotator-clear"
            >
              Clear
            </Button>
            <Button
              variant="outline"
              onClick={() => setAnnotatorOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={saveAnnotation}
              disabled={markers.length === 0}
              className="bg-red-600 hover:bg-red-700"
              data-testid="loto-annotator-save"
            >
              <Save className="w-4 h-4 mr-2" /> Save Annotation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
